import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { Button } from "@/components/flexkit/Button";
import { FlexTruncatedText } from "@/components/FlexTruncatedText";
import { cn } from "@/components/podkit/lib/cn";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { VisibleOnActive } from "@/components/ui/VisibleOnActive";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useBranch } from "@/hooks/use-git-reference";
import { useExitTransition } from "@/hooks/use-exit-transition";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import { useGroupedAgentExecutions, type PlainAgentExecution } from "@/queries/agent-queries";
import { useUnarchiveEnvironment } from "@/queries/environment-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useProject } from "@/queries/project-queries";
import { EnvironmentChanges, EnvironmentName } from "@/routes/environments/SidebarEnvironmentList";
import { mostRecentAgentExecution } from "@/utils/agent-executions";
import type { ArchivedEnvironment } from "@/utils/environment";
import {
    getProjectDisplayName,
    groupArchivedEnvironmentsByArchiveDate,
    type GroupedArchivedEnvironments,
} from "@/utils/environment";
import { formatError } from "@/utils/errors";
import { haptic } from "@/utils/haptic";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { useLongPress } from "react-aria";

type ArchivedEnvironmentsListProps = {
    archivedEnvironments?: ArchivedEnvironment[];
    isLoading?: boolean;
    onDelete: (environment: ArchivedEnvironment) => void;
    search?: string;
};

export const ArchivedEnvironmentsList: FC<ArchivedEnvironmentsListProps> = ({
    archivedEnvironments,
    isLoading,
    onDelete,
    search,
}: ArchivedEnvironmentsListProps) => {
    const { data: groupedAgentExecutions } = useGroupedAgentExecutions();
    const { data: organization } = useOrganization();

    const groupedEnvironments: GroupedArchivedEnvironments = useMemo(() => {
        return groupArchivedEnvironmentsByArchiveDate(archivedEnvironments ?? []);
    }, [archivedEnvironments]);

    // Get tier-specific inactive environment auto-archival message
    const getInactiveArchivalMessage = useMemo(() => {
        const tier = organization?.tier;

        if (tier === OrganizationTier.FREE_ONA) {
            return "Environments that are not used for 2 days are automatically archived.";
        } else if (tier === OrganizationTier.CORE) {
            return "Environments that are not used for 3 days are automatically archived.";
        } else {
            // Default message for FREE and ENTERPRISE tiers
            return "Environments that are not used for 7 days are automatically archived.";
        }
    }, [organization?.tier]);

    // Build the complete empty state description
    const emptyStateDescription = useMemo(() => {
        const tier = organization?.tier;
        const archivalMessage = getInactiveArchivalMessage;

        // For FREE_ONA and CORE, don't mention auto-delete since it's not configurable
        if (tier === OrganizationTier.FREE_ONA || tier === OrganizationTier.CORE) {
            return archivalMessage;
        }

        // For other tiers, include auto-delete message
        return `${archivalMessage} Archived environments will be deleted based on your auto-delete setting.`;
    }, [organization?.tier, getInactiveArchivalMessage]);

    return (
        <>
            {isLoading ? (
                <div className="flex flex-grow flex-col gap-2 p-4">
                    <div className="flex flex-col items-center gap-[8px]">
                        <SkeletonBlock className="h-12 w-full" ready={false} />
                        <SkeletonBlock className="h-12 w-full" ready={false} />
                        <SkeletonBlock className="h-12 w-full" ready={false} />
                    </div>
                </div>
            ) : archivedEnvironments && archivedEnvironments.length > 0 ? (
                <div className="flex flex-col gap-4 py-2 pl-2 pr-2 [@supports(-moz-appearance:none)]:pr-0">
                    {groupedEnvironments.oneToTwoWeeks.length > 0 && (
                        <div className="flex flex-col gap-2 py-2">
                            <Text className="pl-[8px] text-sm font-bold text-content-secondary">1 week ago</Text>
                            {groupedEnvironments.oneToTwoWeeks.map((env) => (
                                <ArchivedEnvironmentItem
                                    key={env.environment.id}
                                    archivedEnvironment={env}
                                    onDelete={onDelete}
                                    agentExecutions={
                                        groupedAgentExecutions?.environmentScoped.get(env.environment.id) ?? []
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {groupedEnvironments.twoWeeksToMonth.length > 0 && (
                        <div className="flex flex-col gap-2 py-2">
                            <Text className="pl-[8px] text-sm font-bold text-content-secondary">2 weeks ago</Text>
                            {groupedEnvironments.twoWeeksToMonth.map((env) => (
                                <ArchivedEnvironmentItem
                                    key={env.environment.id}
                                    archivedEnvironment={env}
                                    onDelete={onDelete}
                                    agentExecutions={
                                        groupedAgentExecutions?.environmentScoped.get(env.environment.id) ?? []
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {groupedEnvironments.olderThanMonth.length > 0 && (
                        <div className="flex flex-col gap-2 py-2">
                            <Text className="pl-[8px] text-sm font-bold text-content-secondary">1 month ago</Text>
                            {groupedEnvironments.olderThanMonth.map((env) => (
                                <ArchivedEnvironmentItem
                                    key={env.environment.id}
                                    archivedEnvironment={env}
                                    onDelete={onDelete}
                                    agentExecutions={
                                        groupedAgentExecutions?.environmentScoped.get(env.environment.id) ?? []
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            ) : search && (!archivedEnvironments || archivedEnvironments.length === 0) ? (
                <EmptyStateCard
                    title="No results found"
                    description="No archived environments match your search criteria."
                />
            ) : (
                <EmptyStateCard title="Nothing archived" description={emptyStateDescription} />
            )}
        </>
    );
};

type ArchivedEnvironmentItemProps = {
    archivedEnvironment: ArchivedEnvironment;
    onDelete: (archivedEnvironment: ArchivedEnvironment) => void;
    agentExecutions: PlainAgentExecution[];
};

const ArchivedEnvironmentItem: FC<ArchivedEnvironmentItemProps> = ({
    archivedEnvironment,
    onDelete,
    agentExecutions,
}) => {
    const agentExecution = useMemo(() => mostRecentAgentExecution(agentExecutions), [agentExecutions]);
    const environmentName = useEnvironmentName(archivedEnvironment.environment, agentExecution);
    const branchName = useBranch(archivedEnvironment.environment) ?? "main";
    const unarchiveEnvironment = useUnarchiveEnvironment();
    const { toast } = useToast();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    // Keep the trigger button visible while the dropdown exit-animates.
    const { shouldRender: isDropdownVisible } = useExitTransition(isDropdownOpen, { duration: 150 });
    const isPointerCoarse = useIsPointerCoarse();

    const { longPressProps } = useLongPress({
        onLongPress: () => {
            haptic("confirm");
            setIsDropdownOpen(true);
        },
        accessibilityDescription: "Long press to open environment actions menu",
    });

    const onUnarchive = useCallback(async () => {
        try {
            await unarchiveEnvironment.mutateAsync(archivedEnvironment.environment.id);
            toast({ title: "Environment unarchived" });
        } catch (err) {
            toast({
                title: "Failed to unarchive environment",
                description: formatError(err),
            });
        }
    }, [archivedEnvironment.environment.id, toast, unarchiveEnvironment]);

    const { data: project, isError: isProjectError } = useProject(archivedEnvironment.projectId);
    const isProjectMissing = !!archivedEnvironment.projectId && isProjectError;

    return (
        <div
            className="group flex flex-row items-center rounded-md p-2 px-[8px] transition-colors hover:bg-surface-glass"
            data-testid={`archived-environment-item-${archivedEnvironment.environment.id}`}
            {...(isPointerCoarse ? longPressProps : undefined)}
        >
            <div className="flex min-w-0 flex-1 flex-col items-start">
                <div className="mb-1 h-5 w-full truncate text-base font-bold">
                    <EnvironmentName
                        size="base"
                        name={environmentName}
                        branchName={branchName}
                        agentExecution={agentExecution}
                    />
                </div>
                <div className="flex w-full min-w-0 flex-row items-center gap-1 pr-1 text-content-secondary">
                    <span className="truncate text-sm">
                        {archivedEnvironment.repoFullName ? (
                            <FlexTruncatedText
                                text={archivedEnvironment.repoFullName}
                                className="text-sm font-medium"
                            />
                        ) : isProjectMissing ? (
                            "Deleted project"
                        ) : (
                            <SkeletonText size="sm" className="w-32" ready={!!project}>
                                <span className={cn("inline-block max-w-full truncate text-content-muted")}>
                                    {getProjectDisplayName(project, isProjectMissing)}
                                </span>
                            </SkeletonText>
                        )}
                    </span>
                    <EnvironmentChanges git={archivedEnvironment.environment.status?.content?.git} />
                </div>
            </div>
            <VisibleOnActive
                className={cn("flex flex-row items-center", { "not-sr-only": isDropdownVisible || isPointerCoarse })}
            >
                <DropdownMenu modal={true} onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                        <span className="flex items-center">
                            <Tooltip content="Unarchive, delete, and more" usePortal>
                                <Button
                                    LeadingIcon={IconEllipsisHorizontal}
                                    size="xs"
                                    variant="ghost"
                                    type="button"
                                    aria-label="Unarchive, delete, and more"
                                    className="border-0 text-content-secondary"
                                    data-state={isDropdownOpen ? "open" : "closed"}
                                    data-open={isDropdownOpen ? "open" : "closed"}
                                />
                            </Tooltip>
                        </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="min-w-[180px]">
                        <DropdownMenuItem
                            onClick={onUnarchive}
                            disabled={unarchiveEnvironment.isPending}
                            data-tracking-id="unarchive-environment-archived-item"
                        >
                            Unarchive
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            variant="destructive"
                            onClick={() => onDelete(archivedEnvironment)}
                            data-tracking-id="delete-environment-archived-item"
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </VisibleOnActive>
        </div>
    );
};

type EmptyStateCardProps = {
    title: string;
    description: string;
};

const EmptyStateCard: FC<EmptyStateCardProps> = ({ title, description }) => {
    return (
        <div className="flex flex-col gap-2 p-4">
            <div className="rounded-xl bg-surface-03 p-4">
                <div className="flex flex-col items-center gap-[8px]">
                    <div className="text-lg-bold font-bold">{title}</div>
                    <div className="text-center text-base text-content-secondary">{description}</div>
                </div>
            </div>
        </div>
    );
};
